import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import type { OpenParams, Panel, PreviewPanelsAction, PreviewPanelsApiState } from '../types';

import { doPreviewPanelTypesMatch } from './utils';

const noop = (props?: any) => {};

const initialState: PreviewPanelsApiState = {
	// Configured when opening a panel.
	panel: null,
	width: 400,
	onOpen: noop,
	onClose: noop,
};

export const ANALYTICS_CHANNEL = 'previewPanel';

export const PreviewPanelsContext = createContext<
	| {
			close: (onClose?: () => void) => void;
			open: (params: OpenParams) => void;
			setPanelWidth: (width: number) => void;
			state: PreviewPanelsApiState;
	  }
	| undefined
>(undefined);

const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'SET_PANEL_WIDTH':
			return { ...state, width: action.width };
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			state.panel && state?.onPreviewPanelClose?.(state.panel);
			return {
				...state,
				panel: null,
				onOpen: noop,
				onClose: noop,
			};
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel?.id === params.panel.id) {
				return state;
			}

			/*
				When opening a new preview panel, if the current panel is of the same type we don't
				want to close the panel, but rather swap out the content. If the panel types don't match
				we close the current panel and open the new one.
				ex: A Goals panel is already open, and user tries to open a different Goals panel.
				Parent Products must pass their preview panel close functions to the Preview Panel provider
				via the `onPreviewPanelClose` prop.
			*/
			if (state.panel && !doPreviewPanelTypesMatch(state.panel, params.panel)) {
				// Close the active panel.
				state.onPreviewPanelClose?.(state.panel);
			}
			state.panel && state.onClose();

			// Open the new panel.
			params.onOpen?.();
			state?.onPreviewPanelOpen?.(params.panel);
			return {
				...state,
				panel: params.panel,
				width: params.width ?? state.width,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	onPreviewPanelOpen,
	onPreviewPanelClose,
	children,
}: {
	children: ReactNode;
	onPreviewPanelClose?: (panel: Panel) => void;
	onPreviewPanelOpen?: (panel: Panel) => void;
}) => {
	const [state, dispatch] = useReducer(previewPanelsReducer, {
		...initialState,
		onPreviewPanelOpen,
		onPreviewPanelClose,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const setPanelWidth = useCallback(
		(width: number) => {
			if (fg('platform_global_preview_panels_resize_analytics')) {
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					action: 'resize',
					actionSubject: 'resizer',
					actionSubjectId: 'previewPanelResizer',
					attributes: {
						initialPanelWidth: state.width,
						newPanelWidth: width,
					},
				}).fire(ANALYTICS_CHANNEL);
			}

			dispatch?.({ type: 'SET_PANEL_WIDTH', width });
		},
		[createAnalyticsEvent, dispatch, state],
	);

	const close = useCallback(
		(onClose?: () => void) => dispatch?.({ type: 'CLOSE', onClose }),
		[dispatch],
	);

	const open = useCallback(
		(params: OpenParams) => dispatch?.({ type: 'OPEN', params }),
		[dispatch],
	);

	const context = useMemo(
		() => ({
			state,
			open,
			close,
			setPanelWidth,
		}),
		[state, open, close, setPanelWidth],
	);

	return <PreviewPanelsContext.Provider value={context}>{children}</PreviewPanelsContext.Provider>;
};

// Hook to use the context
export const usePreviewPanelsContext = () => {
	const context = useContext(PreviewPanelsContext);
	return context;
};
